import React, { useEffect, useRef, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Stack,
  Box,
  Typography,
  IconButton,
  Grid,
  Autocomplete,
  CircularProgress,
  Paper,
  Chip,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ConfirmationNumberIcon from "@mui/icons-material/ConfirmationNumberOutlined";
import BusinessIcon from "@mui/icons-material/BusinessOutlined";
import PersonIcon from "@mui/icons-material/PersonOutlined";
import PhoneIcon from "@mui/icons-material/PhoneOutlined";
import PlaceIcon from "@mui/icons-material/PlaceOutlined";
import { useDispatch, useSelector } from "react-redux";
import toast from "react-hot-toast";
import {
  createTicket,
  fetchHeadings,
  fetchResolvedByOptions,
  fetchAdminUsersForTickets,
  fetchTickets,
} from "./ticketsSlice";
import FileGallery from "./FileGallery";
import api from "../../services/api";

const fieldSx = { "& .MuiInputBase-input": { fontSize: "0.8125rem" } };

// Easy-to-identify style for dropdowns / autocompletes — teal border +
// teal chevron + teal label, no background fill.
const dropdownFieldSx = {
  ...fieldSx,
  "& .MuiInputLabel-root": {
    color: "primary.main",
    fontWeight: 600,
  },
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px",
    "& fieldset": {
      borderColor: "rgba(13,148,136,0.45)",
      borderWidth: "1.5px",
    },
    "&:hover": {
      "& fieldset": { borderColor: "primary.main" },
    },
    "&.Mui-focused": {
      "& fieldset": { borderColor: "primary.main", borderWidth: "1.5px" },
    },
  },
  "& .MuiInputBase-input": {
    fontSize: "0.8125rem",
    fontWeight: 500,
  },
  "& .MuiAutocomplete-popupIndicator, & .MuiSelect-icon": {
    color: "primary.main",
  },
};

const dropdownPopupSlotProps = {
  paper: {
    elevation: 4,
    sx: {
      borderRadius: "8px",
      border: "1px solid",
      borderColor: "divider",
      mt: 0.5,
    },
  },
  listbox: {
    sx: {
      py: 0,
      "& .MuiAutocomplete-option": {
        fontSize: "0.8125rem",
        py: 0.75,
        borderBottom: "1px solid",
        borderColor: "divider",
        color: "text.primary",
        "&:last-child": { borderBottom: 0 },
        "&:hover": { backgroundColor: "primary.light", color: "text.primary" },
        "&.Mui-focused": {
          backgroundColor: "primary.light !important",
          color: "text.primary !important",
        },
        // Selected option keeps dark text on a soft teal background
        "&[aria-selected='true']": {
          backgroundColor: "primary.light !important",
          color: "text.primary !important",
          fontWeight: 600,
        },
      },
    },
  },
};

// MUI <Select> uses MenuProps instead of slotProps for the popup
const selectMenuProps = {
  PaperProps: {
    elevation: 4,
    sx: {
      borderRadius: "8px",
      border: "1px solid",
      borderColor: "divider",
      mt: 0.5,
      "& .MuiMenuItem-root": {
        fontSize: "0.8125rem",
        py: 0.75,
        borderBottom: "1px solid",
        borderColor: "divider",
        color: "text.primary",
        "&:last-child": { borderBottom: 0 },
        "&:hover": { backgroundColor: "primary.light", color: "text.primary" },
        "&.Mui-selected": {
          backgroundColor: "primary.light !important",
          color: "text.primary !important",
          fontWeight: 600,
        },
      },
    },
  },
};

const STATUS_OPTIONS = [
  { value: "open", label: "Open" },
  { value: "ongoing", label: "Ongoing" },
  { value: "waiting_user_feedback", label: "Waiting for user feedback" },
  { value: "waiting_material", label: "Waiting for material" },
  { value: "waiting_service", label: "Waiting for service person" },
  { value: "resolved", label: "Resolved" },
  { value: "removed", label: "Removed" },
];

const PRIORITY_OPTIONS = [
  { value: "low", label: "Low" },
  { value: "medium", label: "Medium" },
  { value: "high", label: "High" },
  { value: "critical", label: "Critical" },
];

const empty = () => ({
  imei: "",
  heading: "",
  status: "open",
  priority: "medium",
  resolvedBy: "",
  responsiblePersonId: "",
  updateText: "",
});

export default function TicketCreateDialog({
  open,
  onClose,
  prefillImei,
  query,
}) {
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { saving, headings, resolvedByOptions, adminUsers } = useSelector(
    (s) => s.tickets
  );
  const [form, setForm] = useState(empty());
  const [files, setFiles] = useState([]);
  const [selected, setSelected] = useState(null);
  const [acInput, setAcInput] = useState("");
  const [acOptions, setAcOptions] = useState([]);
  const [acLoading, setAcLoading] = useState(false);
  const acTimerRef = useRef(null);
  const acReqIdRef = useRef(0);

  useEffect(() => {
    if (open) {
      dispatch(fetchHeadings());
      dispatch(fetchResolvedByOptions());
      dispatch(fetchAdminUsersForTickets());
      setForm({ ...empty(), imei: prefillImei || "" });
      setFiles([]);
      setSelected(null);
      setAcInput(prefillImei || "");
      setAcOptions([]);
    }
  }, [open, prefillImei, dispatch]);

  // Debounced search: company name, IMEI (motor/oht), operator, district, etc.
  useEffect(() => {
    if (acTimerRef.current) clearTimeout(acTimerRef.current);
    const q = (acInput || "").trim();
    if (!q || q.length < 2) {
      setAcOptions([]);
      setAcLoading(false);
      return;
    }
    setAcLoading(true);
    acTimerRef.current = setTimeout(async () => {
      const reqId = ++acReqIdRef.current;
      try {
        const { data } = await api.get("/tickets/search-imei", {
          params: { q },
        });
        if (reqId !== acReqIdRef.current) return; // stale
        setAcOptions(data?.data?.items || []);
      } catch {
        if (reqId === acReqIdRef.current) setAcOptions([]);
      } finally {
        if (reqId === acReqIdRef.current) setAcLoading(false);
      }
    }, 300);
    return () => acTimerRef.current && clearTimeout(acTimerRef.current);
  }, [acInput]);

  const handlePickOption = (opt) => {
    setSelected(opt);
    setForm((p) => ({ ...p, imei: opt?.imei || "" }));
  };

  // When opened with a prefilled IMEI, auto-select the matching option once
  // the search returns results, so the info card pops up immediately.
  useEffect(() => {
    if (!open || !prefillImei || selected) return;
    const match = acOptions.find((o) => o.imei === prefillImei);
    if (match) handlePickOption(match);
  }, [open, prefillImei, selected, acOptions]);

  const set = (key, value) => setForm((p) => ({ ...p, [key]: value }));

  const submit = async (e) => {
    e?.preventDefault();
    if (!form.imei.trim() || !form.heading.trim()) {
      toast.error("IMEI and Heading are required");
      return;
    }
    if (form.status === "resolved" && !form.resolvedBy) {
      toast.error("Resolved By is required when status is Resolved");
      return;
    }

    const fd = new FormData();
    fd.append("imei", form.imei.trim().replace(/\s+/g, ""));
    fd.append("heading", form.heading.trim());
    fd.append("status", form.status);
    fd.append("priority", form.priority || "medium");
    fd.append("resolvedBy", form.resolvedBy);
    fd.append("updateText", form.updateText);
    if (form.responsiblePersonId) {
      fd.append("responsiblePerson", JSON.stringify({ id: form.responsiblePersonId }));
    }
    files.forEach((f) => fd.append("files", f));

    const res = await dispatch(createTicket(fd));
    if (createTicket.fulfilled.match(res)) {
      toast.success("Ticket created");
      onClose();
      dispatch(fetchTickets(query));
    } else {
      toast.error(res.payload || "Save failed");
    }
  };

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : "14px",
          overflow: "hidden",
        },
      }}
    >
      {/* Gradient header band — matches the polished User / Device / Audit
          dialogs. */}
      <Box
        sx={{
          position: "relative",
          px: 2,
          pt: 2.5,
          pb: 2,
          background:
            "linear-gradient(135deg, #0d9488 0%, #14b8a6 50%, #2dd4bf 100%)",
          color: "common.white",
          flexShrink: 0,
        }}
      >
        <IconButton
          onClick={onClose}
          disabled={saving}
          size="small"
          sx={{
            position: "absolute",
            top: 8,
            right: 8,
            color: "common.white",
            backgroundColor: "rgba(255,255,255,0.15)",
            "&:hover": { backgroundColor: "rgba(255,255,255,0.25)" },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: "12px",
              bgcolor: "rgba(255,255,255,0.22)",
              border: "2px solid rgba(255,255,255,0.35)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <ConfirmationNumberIcon />
          </Box>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: "1rem",
                letterSpacing: "-0.01em",
                lineHeight: 1.15,
              }}
            >
              New Ticket
            </Typography>
            <Typography
              sx={{
                fontSize: "0.75rem",
                opacity: 0.92,
                mt: 0.25,
                pr: 4,
              }}
            >
              Filed against a device IMEI — company is auto-resolved
            </Typography>
          </Box>
        </Stack>
      </Box>

      <form onSubmit={submit}>
        <DialogContent sx={{ p: 1.5 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Autocomplete
                size="small"
                fullWidth
                freeSolo
                options={acOptions}
                loading={acLoading}
                filterOptions={(x) => x}
                value={selected}
                inputValue={acInput}
                onInputChange={(_, v, reason) => {
                  setAcInput(v);
                  if (reason === "input") {
                    // user typing — clear current selection until they pick one
                    if (selected) {
                      setSelected(null);
                      setForm((p) => ({ ...p, imei: "" }));
                    }
                  }
                }}
                onChange={(_, v) => {
                  if (!v || typeof v === "string") {
                    setSelected(null);
                    setForm((p) => ({
                      ...p,
                      imei: typeof v === "string" ? v.replace(/\s+/g, "") : "",
                    }));
                    return;
                  }
                  handlePickOption(v);
                }}
                getOptionLabel={(o) =>
                  typeof o === "string"
                    ? o
                    : o
                    ? `${o.company_name || "—"} — ${
                        o.type === "motor" ? "Motor" : "OHT"
                      } · ${o.imei}`
                    : ""
                }
                isOptionEqualToValue={(a, b) =>
                  a && b && a.key === b.key
                }
                disabled={Boolean(prefillImei)}
                noOptionsText={
                  acInput.trim().length < 2
                    ? "Type 2+ characters to search"
                    : "No matches"
                }
                renderOption={(props, opt) => (
                  <li {...props} key={opt.key}>
                    <Stack sx={{ width: "100%", minWidth: 0 }} spacing={0.25}>
                      <Stack direction="row" alignItems="center" spacing={0.75} flexWrap="wrap" useFlexGap>
                        <Chip
                          label={opt.type === "motor" ? "MOTOR" : "OHT"}
                          size="small"
                          sx={{
                            height: 17,
                            fontSize: "0.625rem",
                            fontWeight: 700,
                            backgroundColor:
                              opt.type === "motor" ? "#0e7490" : "#0d9488",
                            color: "#fff",
                          }}
                        />
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 600, fontSize: "0.8125rem" }}
                        >
                          {opt.company_name || "—"}
                        </Typography>
                        {opt.manufacturing_id != null && (
                          <Typography
                            variant="caption"
                            sx={{
                              fontFamily: "monospace",
                              color: "primary.main",
                              fontSize: "0.6875rem",
                            }}
                          >
                            #{opt.manufacturing_id}
                          </Typography>
                        )}
                      </Stack>
                      <Typography
                        variant="caption"
                        sx={{ fontFamily: "monospace", color: "text.secondary", fontSize: "0.7rem" }}
                      >
                        IMEI {opt.imei}
                      </Typography>
                      {(opt.operator_name || opt.operator_phone) && (
                        <Typography
                          variant="caption"
                          sx={{ color: "text.secondary", fontSize: "0.7rem" }}
                        >
                          {opt.operator_name || "—"}
                          {opt.operator_phone ? ` · ${opt.operator_phone}` : ""}
                        </Typography>
                      )}
                    </Stack>
                  </li>
                )}
                slotProps={dropdownPopupSlotProps}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="IMEI / Company *"
                    placeholder="Search by company name, IMEI, operator…"
                    sx={dropdownFieldSx}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {acLoading ? (
                            <CircularProgress color="inherit" size={14} />
                          ) : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            </Grid>
            {/* === Selected company info card === */}
            {selected && (
              <Grid item xs={12}>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 1.25,
                    borderColor: "primary.main",
                    backgroundColor: "primary.light",
                  }}
                >
                  <Stack
                    direction={{ xs: "column", sm: "row" }}
                    spacing={1.25}
                    alignItems={{ xs: "flex-start", sm: "center" }}
                    justifyContent="space-between"
                  >
                    <Stack spacing={0.5} sx={{ minWidth: 0, flex: 1 }}>
                      <Stack direction="row" alignItems="center" spacing={0.75} flexWrap="wrap" useFlexGap>
                        <BusinessIcon sx={{ fontSize: 15, color: "primary.main" }} />
                        <Typography
                          variant="subtitle2"
                          sx={{ fontWeight: 700, fontSize: "0.8125rem", color: "primary.main" }}
                        >
                          {selected.company_name || "—"}
                        </Typography>
                        {selected.manufacturing_id != null && (
                          <Chip
                            label={`#${selected.manufacturing_id}`}
                            size="small"
                            sx={{
                              height: 18,
                              fontSize: "0.625rem",
                              fontFamily: "monospace",
                              backgroundColor: "#fff",
                              color: "primary.main",
                              fontWeight: 600,
                            }}
                          />
                        )}
                        <Chip
                          label={selected.type === "motor" ? "MOTOR" : "OHT"}
                          size="small"
                          sx={{
                            height: 18,
                            fontSize: "0.625rem",
                            fontWeight: 700,
                            backgroundColor:
                              selected.type === "motor" ? "#0e7490" : "#0d9488",
                            color: "#fff",
                          }}
                        />
                        <Typography
                          variant="caption"
                          sx={{ fontFamily: "monospace", fontSize: "0.7rem" }}
                        >
                          IMEI {selected.imei}
                        </Typography>
                      </Stack>

                      {(selected.district || selected.block || selected.panchayat) && (
                        <Stack direction="row" alignItems="center" spacing={0.5}>
                          <PlaceIcon sx={{ fontSize: 13, color: "text.secondary" }} />
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.7rem" }}>
                            {[selected.district, selected.block, selected.panchayat]
                              .filter(Boolean)
                              .join(" · ")}
                          </Typography>
                        </Stack>
                      )}

                      {(selected.operator_name || selected.operator_phone) && (
                        <Stack direction="row" alignItems="center" spacing={0.75} flexWrap="wrap" useFlexGap>
                          <PersonIcon sx={{ fontSize: 13, color: "text.secondary" }} />
                          <Typography
                            variant="caption"
                            sx={{ fontSize: "0.75rem", color: "text.primary", fontWeight: 500 }}
                          >
                            {selected.operator_name || "—"}
                          </Typography>
                          {selected.operator_phone && (
                            <>
                              <PhoneIcon sx={{ fontSize: 13, color: "text.secondary" }} />
                              <Typography
                                variant="caption"
                                sx={{ fontSize: "0.75rem", fontFamily: "monospace" }}
                              >
                                {selected.operator_phone}
                              </Typography>
                            </>
                          )}
                        </Stack>
                      )}
                    </Stack>
                  </Stack>
                </Paper>
              </Grid>
            )}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                select
                label="Heading *"
                value={form.heading || ""}
                onChange={(e) => set("heading", e.target.value)}
                SelectProps={{ MenuProps: selectMenuProps }}
                sx={dropdownFieldSx}
              >
                {headings.map((h) => (
                  <MenuItem key={h._id || h.name} value={h.name} sx={{ fontSize: "0.8125rem" }}>
                    {h.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                select
                label="Status"
                value={form.status}
                onChange={(e) => set("status", e.target.value)}
                sx={dropdownFieldSx}
                SelectProps={{ MenuProps: selectMenuProps }}
              >
                {STATUS_OPTIONS.map((s) => (
                  <MenuItem key={s.value} value={s.value} sx={{ fontSize: "0.8125rem" }}>
                    {s.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                select
                label="Priority"
                value={form.priority}
                onChange={(e) => set("priority", e.target.value)}
                sx={dropdownFieldSx}
                SelectProps={{ MenuProps: selectMenuProps }}
              >
                {PRIORITY_OPTIONS.map((p) => (
                  <MenuItem
                    key={p.value}
                    value={p.value}
                    sx={{ fontSize: "0.8125rem" }}
                  >
                    {p.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            {/* Resolved By is only relevant for tickets being created as
                "resolved" (rare but possible — e.g. logging an already-closed
                issue retroactively). Hidden for every other status. */}
            {form.status === "resolved" && (
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  select
                  label="Resolved By *"
                  value={form.resolvedBy || ""}
                  onChange={(e) => set("resolvedBy", e.target.value)}
                  SelectProps={{ MenuProps: selectMenuProps }}
                  sx={dropdownFieldSx}
                >
                  <MenuItem value="" sx={{ fontSize: "0.8125rem" }}>—</MenuItem>
                  {resolvedByOptions.map((o) => (
                    <MenuItem
                      key={o._id || o.name}
                      value={o.name}
                      sx={{ fontSize: "0.8125rem" }}
                    >
                      {o.name}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            )}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                select
                label="Responsible Person"
                value={form.responsiblePersonId || ""}
                onChange={(e) => set("responsiblePersonId", e.target.value)}
                SelectProps={{ MenuProps: selectMenuProps }}
                sx={dropdownFieldSx}
              >
                <MenuItem value="" sx={{ fontSize: "0.8125rem" }}>—</MenuItem>
                {adminUsers.map((u) => (
                  <MenuItem key={u._id} value={u._id} sx={{ fontSize: "0.8125rem" }}>
                    {u.name || u.username || "—"}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                minRows={3}
                size="small"
                label="Initial note (optional)"
                value={form.updateText}
                onChange={(e) => set("updateText", e.target.value)}
                sx={fieldSx}
              />
            </Grid>
            <Grid item xs={12}>
              <FileGallery
                existing={[]}
                newFiles={files}
                addFiles={(arr) => setFiles((p) => [...p, ...arr])}
                removeNew={(i) => setFiles((p) => p.filter((_, idx) => idx !== i))}
                emptyText="Attach screenshots, videos or PDFs (optional)"
              />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions
          sx={{
            p: 1.5,
            borderTop: "1px solid",
            borderColor: "divider",
            backgroundColor: "background.paper",
            gap: 1,
            justifyContent: { xs: "stretch", sm: "flex-end" },
          }}
        >
          <Button
            onClick={onClose}
            variant="outlined"
            disabled={saving}
            sx={{ borderRadius: "8px", flex: { xs: 1, sm: "0 0 auto" } }}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={saving}
            startIcon={
              saving ? <CircularProgress size={14} color="inherit" /> : null
            }
            sx={{
              borderRadius: "8px",
              flex: { xs: 1, sm: "0 0 auto" },
              boxShadow: "0 4px 10px -4px rgba(13,148,136,0.55)",
            }}
          >
            {saving ? "Saving…" : "Create Ticket"}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
